import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, labelStyle } from '../../theme';

// ── Tooth layout (FDI numbering) ──
const UPPER_RIGHT = ['18','17','16','15','14','13','12','11'];
const UPPER_LEFT  = ['21','22','23','24','25','26','27','28'];
const LOWER_LEFT  = ['31','32','33','34','35','36','37','38'];
const LOWER_RIGHT = ['48','47','46','45','44','43','42','41'];

const CONDITIONS = [
  { key:'normal',      label:'Normal',      color:'#e5e9f0', textColor:'#6b7785' },
  { key:'caries',      label:'Caries',      color:'#ef4444', textColor:'#fff'    },
  { key:'filled',      label:'Filled',      color:'#3b82f6', textColor:'#fff'    },
  { key:'rct',         label:'RCT',         color:'#f59e0b', textColor:'#fff'    },
  { key:'crown',       label:'Crown',       color:'#8b5cf6', textColor:'#fff'    },
  { key:'implant',     label:'Implant',     color:'#10b981', textColor:'#fff'    },
  { key:'missing',     label:'Missing',     color:'#1a2332', textColor:'#fff'    },
  { key:'fracture',    label:'Fracture',    color:'#ec4899', textColor:'#fff'    },
  { key:'sensitivity', label:'Sensitivity', color:'#06b6d4', textColor:'#fff'    },
  { key:'bridge',      label:'Bridge',      color:'#6366f1', textColor:'#fff'    },
  { key:'veneer',      label:'Veneer',      color:'#14b8a6', textColor:'#fff'    },
];

const SURFACES = ['M','D','O','B','L'];

const getConditionStyle = (condKey) => {
  const c = CONDITIONS.find(x => x.key === condKey) || CONDITIONS[0];
  return { bg: c.color, text: c.textColor, label: c.label };
};

/**
 * Odontogram — interactive 32-tooth dental chart.
 *
 * Props:
 *   patientId — ObjectId
 *   API_BASE  — base API url
 */
const Odontogram = ({ patientId, API_BASE }) => {
  const [toothData, setToothData] = useState({});  // { "14": { condition, surfaces, notes, _id } }
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [form, setForm] = useState({ condition:'normal', surfaces:[], notes:'' });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchChart = async () => {
    try {
      const r = await axios.get(`${API_BASE}/tooth-chart/patient/${patientId}`);
      const map = {};
      r.data.forEach(t => { map[t.tooth] = t; });
      setToothData(map);
    } catch (e) { /* silent */ }
  };

  const fetchHistory = async (tooth) => {
    try {
      const r = await axios.get(`${API_BASE}/tooth-chart/patient/${patientId}/tooth/${tooth}`);
      setHistory(r.data);
    } catch (e) { setHistory([]); }
  };

  useEffect(() => { fetchChart(); }, [patientId]); // eslint-disable-line

  const handleToothClick = (tooth) => {
    setSelectedTooth(tooth);
    const existing = toothData[tooth];
    setForm({
      condition: existing?.condition || 'normal',
      surfaces:  existing?.surfaces  || [],
      notes:     existing?.notes     || '',
    });
    fetchHistory(tooth);
  };

  const toggleSurface = (s) => {
    setForm(f => ({
      ...f,
      surfaces: f.surfaces.includes(s) ? f.surfaces.filter(x => x !== s) : [...f.surfaces, s],
    }));
  };

  const handleSave = async () => {
    if (!selectedTooth) return;
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/tooth-chart`, {
        patientId,
        tooth: selectedTooth,
        condition: form.condition,
        surfaces: form.surfaces,
        notes: form.notes,
      });
      fetchChart();
      fetchHistory(selectedTooth);
    } catch (e) { alert('Save failed'); }
    setSaving(false);
  };

  const ToothCell = ({ tooth, label }) => {
    const data = toothData[tooth];
    const cond = data?.condition || 'normal';
    const style = getConditionStyle(cond);
    const isSelected = selectedTooth === tooth;
    const isMissing = cond === 'missing';

    return (
      <div
        onClick={() => handleToothClick(tooth)}
        style={{
          width:'42px', height:'52px',
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
          borderRadius:'8px',
          cursor:'pointer',
          border: isSelected ? `3px solid ${theme.primary}` : '2px solid transparent',
          backgroundColor: style.bg,
          color: style.text,
          fontWeight:'700',
          fontSize:'0.78rem',
          transition:'all 0.15s',
          opacity: isMissing ? 0.45 : 1,
          position:'relative',
        }}
        title={`${tooth} — ${style.label}`}>
        <div style={{fontSize:'0.65rem', opacity:0.7, lineHeight:1}}>{label || tooth}</div>
        <div style={{fontSize:'0.88rem', lineHeight:1.2}}>{tooth}</div>
        {data?.surfaces?.length > 0 && (
          <div style={{fontSize:'0.5rem', lineHeight:1, marginTop:'1px', opacity:0.8}}>
            {data.surfaces.join('')}
          </div>
        )}
      </div>
    );
  };

  const renderRow = (teeth, label) => (
    <div style={{display:'flex', gap:'4px', justifyContent:'center'}}>
      {teeth.map(t => <ToothCell key={t} tooth={t}/>)}
    </div>
  );

  return (
    <div>
      <h3 style={{color:theme.navy, margin:'0 0 6px'}}>🦷 Dental Chart (Odontogram)</h3>
      <p style={{color:theme.textMuted, fontSize:'0.82rem', margin:'0 0 18px'}}>Click any tooth to view or update its condition</p>

      {/* Chart */}
      <div style={{...cardStyle, marginBottom:'18px', padding:'24px 16px', textAlign:'center'}}>
        {/* Labels */}
        <div style={{display:'flex', justifyContent:'center', gap:'60px', marginBottom:'4px'}}>
          <span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600'}}>UPPER RIGHT</span>
          <span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600'}}>UPPER LEFT</span>
        </div>

        {/* Upper jaw */}
        <div style={{display:'flex', justifyContent:'center', gap:'12px', marginBottom:'8px'}}>
          {renderRow(UPPER_RIGHT)}
          <div style={{width:'2px', backgroundColor:theme.border, margin:'0 4px'}}/>
          {renderRow(UPPER_LEFT)}
        </div>

        {/* Midline */}
        <div style={{height:'2px', backgroundColor:theme.border, margin:'8px auto', width:'80%', maxWidth:'500px'}}/>

        {/* Lower jaw */}
        <div style={{display:'flex', justifyContent:'center', gap:'12px', marginTop:'8px'}}>
          {renderRow(LOWER_RIGHT)}
          <div style={{width:'2px', backgroundColor:theme.border, margin:'0 4px'}}/>
          {renderRow(LOWER_LEFT)}
        </div>

        <div style={{display:'flex', justifyContent:'center', gap:'60px', marginTop:'4px'}}>
          <span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600'}}>LOWER RIGHT</span>
          <span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600'}}>LOWER LEFT</span>
        </div>

        {/* Legend */}
        <div style={{display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'8px', marginTop:'18px'}}>
          {CONDITIONS.map(c => (
            <div key={c.key} style={{display:'flex', alignItems:'center', gap:'4px', fontSize:'0.72rem', color:theme.textMuted}}>
              <div style={{width:'12px', height:'12px', borderRadius:'3px', backgroundColor:c.color, border:'1px solid #ddd'}}/>
              {c.label}
            </div>
          ))}
        </div>
      </div>

      {/* Selected tooth editor */}
      {selectedTooth && (
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'18px'}}>
          {/* Edit form */}
          <div style={{...cardStyle, borderTop:`4px solid ${theme.primary}`}}>
            <h4 style={{margin:'0 0 14px', color:theme.navy}}>
              Tooth #{selectedTooth}
              <span style={{marginLeft:'10px', fontSize:'0.82rem', fontWeight:'500', color:theme.textMuted}}>
                Current: {getConditionStyle(toothData[selectedTooth]?.condition || 'normal').label}
              </span>
            </h4>

            {/* Condition picker */}
            <label style={labelStyle}>Condition</label>
            <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'14px'}}>
              {CONDITIONS.map(c => (
                <button key={c.key}
                  onClick={() => setForm({...form, condition:c.key})}
                  style={{
                    padding:'5px 12px', borderRadius:'14px', border:'none', cursor:'pointer',
                    fontSize:'0.78rem', fontWeight:'700',
                    backgroundColor: form.condition === c.key ? c.color : '#f0f0f0',
                    color: form.condition === c.key ? c.textColor : '#666',
                    boxShadow: form.condition === c.key ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                  }}>
                  {c.label}
                </button>
              ))}
            </div>

            {/* Surface picker */}
            <label style={labelStyle}>Surfaces</label>
            <div style={{display:'flex', gap:'6px', marginBottom:'14px'}}>
              {SURFACES.map(s => (
                <button key={s}
                  onClick={() => toggleSurface(s)}
                  style={{
                    width:'40px', height:'40px', borderRadius:'8px', border:'none', cursor:'pointer',
                    fontWeight:'700', fontSize:'0.9rem',
                    backgroundColor: form.surfaces.includes(s) ? theme.primary : '#e9ecef',
                    color: form.surfaces.includes(s) ? 'white' : '#555',
                  }}>
                  {s}
                </button>
              ))}
            </div>

            {/* Notes */}
            <label style={labelStyle}>Notes</label>
            <input value={form.notes} onChange={e => setForm({...form, notes:e.target.value})}
              placeholder="Optional clinical notes..."
              style={{...inputStyle, marginBottom:'14px'}}/>

            <button onClick={handleSave} disabled={saving}
              style={{
                padding:'10px 22px', backgroundColor:theme.primary, color:'white',
                border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600',
              }}>
              {saving ? 'Saving...' : 'Save Observation'}
            </button>
          </div>

          {/* History */}
          <div style={cardStyle}>
            <h4 style={{margin:'0 0 14px', color:theme.navy}}>History — Tooth #{selectedTooth}</h4>
            {history.length === 0 ? (
              <p style={{color:'#aaa', fontSize:'0.85rem'}}>No history recorded for this tooth</p>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                {history.map(h => {
                  const s = getConditionStyle(h.condition);
                  return (
                    <div key={h._id} style={{
                      display:'flex', alignItems:'center', gap:'10px',
                      padding:'10px 12px', backgroundColor:'#f8f9fa', borderRadius:'8px',
                      borderLeft:`4px solid ${s.bg}`,
                    }}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:'600', color:theme.navy, fontSize:'0.85rem'}}>
                          {s.label}
                          {h.surfaces?.length > 0 && <span style={{marginLeft:'6px', color:theme.textMuted, fontWeight:'400'}}>({h.surfaces.join(', ')})</span>}
                        </div>
                        {h.notes && <div style={{fontSize:'0.78rem', color:theme.textMuted, marginTop:'2px'}}>{h.notes}</div>}
                      </div>
                      <div style={{fontSize:'0.72rem', color:theme.textSubtle, whiteSpace:'nowrap'}}>
                        {new Date(h.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Odontogram;
